/**
 * 이상한 방 - 라운드 상태 머신 (순수 리듀서)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.3절(탄환 경제) · 4.4절(정답 방)
 *           `이상한방-상태모델-v0.1.md` 6절(판정) · 11.3절(서버가 쥐는 것)
 *
 * ## 왜 리듀서인가
 *
 * 11.3절이 서버의 몫으로 못박은 것 - 페이즈 전환·타이머·정답 판정·탄환 카운트·
 * 정답 방 입장 허가·점수 - 가 전부 이 파일이 소유한다. 지금은 싱글이라 클라이언트에서 돌지만
 * **로직 자체는 이미 서버 코드**이며, 멀티로 옮길 때 이 파일은 이사만 하면 된다.
 * 산술 일부는 리프로 나가 있다 - 판정·점수는 `hunt`, 원래 방 타이머는 `originVisit`, 리빌 길이는
 * `phaseClock`. 매치 리듀서와 **같은 함수**를 쓰기 위해 뽑은 것이고, 서버 몫이라는 사실은 변하지
 * 않는다(리프도 전부 `core`만 본다). 이 파일에 남는 것은 전이·게이트·효과 방출이다.
 *
 * 그러기 위한 조건이 순수성이다:
 *  - 부수효과를 직접 일으키지 않는다. `Effect[]`로 **요청만** 돌려준다(사운드·워프·프록시 동기화).
 *  - 시간을 읽지 않는다. `performance.now()`도 `Date.now()`도 없다 - 시간은 `tick` 이벤트로 들어온다.
 *  - 난수를 만들지 않는다. 시드에서 파생한다. 같은 시드 + 같은 이벤트 열 = 같은 라운드.
 *
 * 이 셋이 지켜지면 라운드 전체를 테스트에서 몇 밀리초에 재생할 수 있고,
 * 그게 `gameState.test.ts`가 게임 규칙의 **명세**로 기능하는 이유다.
 *
 * ## 시드가 둘로 갈린다 - 방은 고정, 변화는 시도마다
 *
 * 직전까지 방과 변화가 **한 시드**에서 나왔다. 레벨은 `seed: 'level-01'`처럼 시드가 고정이므로,
 * 리빌에서 정답 12개를 다 본 뒤 "다시 하기"를 누르면 **똑같은 변화가 똑같은 자리에** 다시 생겼다.
 * 튜토리얼의 재도전이 학습이 아니라 답안 옮겨 적기가 된다는 뜻이다.
 *
 * ```
 *   방   = seed                    ← 레벨마다 고정
 *   변화 = `${seed}:a${attempt}`   ← 재도전마다 달라진다
 * ```
 *
 * **방을 고정하는 것이 핵심이다.** 이 게임의 스킬은 "방을 외우는 것"이고, 같은 방을 반복해서
 * 익숙해지는 것은 치트가 아니라 튜토리얼의 목적 그 자체다(9.5절). 반대로 방까지 매번 바뀌면
 * "1단계"라는 말이 난이도를 보장하지 못하고 단계의 정체성도 사라진다.
 *
 * 결정론은 그대로다 - 시도 번호를 알면 재현된다. 스피드런·일일 챌린지는 `attempt`를 고정하면 된다.
 *
 * ## 탄환 보급이 방에서 계정으로 올라갔다
 *
 * 보급의 자리가 두 번 옮겨졌다. 원래는 정답 방 입장 보상이었고(1차 +4 / 2차 +2), 그 다음에는
 * 방 바닥에 놓인 월드 아이템이었으며, 지금은 **계정 단위 탄창**이다(`magazines.ts`).
 *
 *  - 정답 방 리필을 걷어낸 이유는 그대로다: 원본 방에 가는 이유가 "비교하려고"가 아니라
 *    "탄환이 떨어져서"가 되면 4.4절의 이중 역할이 서로를 흐린다. 리필은 돌아오지 않는다.
 *  - 월드 아이템을 걷어낸 이유는 둘이다. 방에 놓인 물건이 '변화'가 아니면서 화면에 있으면
 *    관찰 게임에서 의심할 대상이 규칙 밖에서 늘어나고, 무엇보다 **라운드가 끝나면 사라지는
 *    자원에는 광고·결제·미니게임 보상을 얹을 수 없다.**
 *
 * 그 결과 규칙 하나가 뒤집힌 것은 유지된다: **잔탄 0이 더 이상 라운드의 끝이 아니다.**
 * 쓸 수 있는 탄창이 남아 있으면 라운드는 계속되고, 그것까지 바닥나야 리빌로 넘어간다(`onMiss`).
 * 다만 부활의 조건이 "방을 훑어 주우러 간다"에서 "HUD 버튼을 누른다"로 바뀌었으므로,
 * 잔탄 0에서 사람이 아무것도 하지 않으면 수색 시간이 다 될 때까지 그대로 서 있게 된다.
 * 그래서 그 순간의 `lowAmmo` 효과는 연출이 아니라 **유일한 안내**다.
 *
 * `magazines`가 라운드 상태 안에 있는 것이 어색해 보일 수 있으나 의도다. 리듀서는 시계도 저장소도
 * 읽지 않으므로 계정 값을 조회할 수단이 없고, 멀티에서 이 판단을 하는 것도 결국 서버(11.3절)다.
 * 저장으로 되돌려 쓰는 일은 세션 레이어가 맡는다.
 *
 * ## 의존성을 왜 주입받는가
 *
 * `changeGen`·`hunt`는 `GameState`를 참조하므로, 여기서 값으로 import 하면 순환 참조가 된다.
 * 더 중요한 이유는 테스트다 - 변화 생성기를 가짜로 바꿀 수 있어야 "탄환 0이면 리빌로 간다"를
 * 방을 391개 만들지 않고 검증할 수 있다.
 */

import type { FloorPlan } from '../core/floorPlan'
import type { Catalog } from '../core/room'
import { generateBaseRoom, type GenerateOptions } from '../core/roomGen'
import { MS_PER_SECOND } from '../core/time'
import type { InstanceId, RoomState } from '../core/types'
import type { Change, GenerateChangesOptions } from './changeGen'
import {
  breaksCombo,
  comboAfter,
  comboMultiplier,
  isPerfectRun,
  NO_COMBO,
  type ComboOutcome,
} from './combo'
/**
 * 사격 게이트와 탄환 값은 판정과 **같은 파일**에 있어야 갈라지지 않으므로 `hunt.ts`에서 가져온다.
 * 값 import인데도 순환이 되지 않는 이유: `hunt.ts`가 이 파일에서 가져가는 것은 전부 `import type`이라
 * 런타임 모듈 그래프에는 gameState → hunt 한 방향만 남는다. (hunt.ts가 값 import을 갖게 되면 깨진다)
 */
import { ammoAfterMiss, ammoCostForOutcome, canShoot, type HitTarget, type ShotOutcome } from './hunt'
/**
 * 원래 방 타이머 산술과 리빌 길이는 매치 리듀서와 **같은 함수**를 써야 하므로 리프로 뽑았다.
 * 여기 남은 것은 효과 방출(`warpTo`·`syncProxies`·소리)과 페이즈 게이트뿐이다.
 */
import {
  canStartOriginVisit,
  enterOriginVisit,
  exitOriginVisit,
  NO_ORIGIN_VISIT,
  tickOriginVisit,
} from './originVisit'
import { revealDurationMs } from './phaseClock'
/** 걸기(M7) 조건은 매치와 **같은 함수**다. 여기는 페이즈 게이트와 효과 방출만 남는다. */
import { canRaise } from './raise'
import type { Tuning } from './tuning'

/** 원래 방 체류 잔여(ms). 산술이 `originVisit.ts`로 갔고, 호출부(`sessionView`)가 여기서 가져가므로 그대로 내보낸다. */
export { originStayRemainingMs } from './originVisit'

/* --- 상수 ------------------------------------------------------------------- */

// 리빌 최소 길이(`REVEAL_MIN_STEPS`)는 리빌 길이 규칙과 함께 `phaseClock.ts`로 갔다.

/** 첫 시도 번호. 재도전할 때마다 1씩 오른다. */
const FIRST_ATTEMPT = 0

/**
 * 변화 시드에 시도 번호를 붙이는 접두사.
 *
 * `:`로 시작하는 이유는 방 시드와 절대 섞이지 않게 하기 위해서다 - 레벨 시드는 `level-01` 꼴이라
 * 구분자가 없으면 `level-01` + `1`과 `level-011` + `` 이 같은 스트림이 된다.
 * `changeGen`이 여기에 `:changes`를 한 번 더 붙이므로 최종 스트림은 `seed:aN:changes`다.
 */
const ATTEMPT_SEED_PREFIX = ':a'

/** 라운드 시작 시점의 탄창 사용 횟수. 계정 보유량과 달리 **라운드마다 0으로 돌아간다.** */
const NO_MAGAZINE_USED = 0

/* --- 타입 ------------------------------------------------------------------- */

/** 라운드 페이즈(컨셉시트 3절). 싱글이므로 '꾸미기'와 '바꾸기'는 시드 생성으로 대체됐다. */
export type Phase = 'watch' | 'hunt' | 'reveal' | 'result'

/** 수색이 끝난 이유. 결과 화면의 문구와 점수 해석이 여기서 갈린다. */
export type HuntEndReason = 'all-found' | 'out-of-ammo' | 'timeout' | 'skipped'

/**
 * 발견 기록. `foundAtMs`는 수색 페이즈 시작 기준이며 속도 보너스의 입력이다(컨셉시트 4.5).
 *
 * `combo`는 **이 발견이 성립시킨 콤보 수**(1부터)이며 점수 배수의 입력이다.
 * 여기에 굳혀 두는 이유가 점수 규칙의 전부다: 배수는 **맞힌 순간의 값으로 고정**되어야 한다.
 * 나중 콤보로 소급 계산하면 오답으로 끊긴 앞부분까지 되살아나 "끊겼다"는 사건이 점수에서 사라진다.
 *
 * 선택 필드인 것도 의도다. 콤보는 이번 개편에서 생긴 값이라 이 필드가 없는 발견 기록
 * (다른 모듈의 테스트 픽스처 등)이 존재하고, 없는 것은 **콤보 없음(1.0배)으로 읽는다** -
 * 모르는 값을 후하게 해석해 점수를 부풀리는 쪽으로는 실패하지 않는다.
 */
export type FoundEntry = Readonly<{
  changeId: string
  foundAtMs: number
  combo?: number
  /** 걸기(M7) 선언 뒤의 발견인가. `combo`와 같은 이유로 발견 시점에 굳고, 없으면 걸지 않은 것으로 읽는다. */
  raised?: boolean
}>

/**
 * 라운드 상태 전체.
 *
 * `original`과 `mutated`를 **둘 다** 들고 있는 것이 이 게임의 핵심 자료구조다(상태모델 2절).
 * 정답 방은 네트워크 문제가 아니라 이 둘 중 무엇을 렌더할지의 문제일 뿐이다(11.3절).
 */
export type GameState = Readonly<{
  phase: Phase
  /**
   * **방 시드.** 이 값은 방만 정한다(변화는 `changeSeedFor`가 따로 파생시킨다).
   * 레벨에서 고정이므로 몇 번을 다시 해도 같은 방이 서고, 그래서 단계의 난이도가 보존된다.
   */
  seed: string
  /**
   * 이 방을 몇 번째로 도전하는가(0부터).
   *
   * **변화 시드에만 섞인다.** 그래서 재도전은 "같은 방 + 새 변화"가 되고,
   * 리빌에서 정답을 본 것이 다음 판의 정답표가 되지 않는다.
   */
  attempt: number
  /** 외우기 페이즈에서 본 방. 정답 방에서 보는 것이 이것이다. */
  original: RoomState
  /** 변화가 적용된 방. 수색 대상. `watch` 동안은 `original`과 같다. */
  mutated: RoomState
  changes: readonly Change[]
  found: readonly FoundEntry[]
  ammo: number
  /**
   * 계정이 들고 있는 탄창 수(`magazines.ts`). **라운드가 아니라 사람의 값이다.**
   *
   * 여기 들어와 있는 이유는 리듀서가 저장소를 읽지 않기 때문이다 - 세션 레이어가 라운드를 만들 때
   * 계정 값을 넣어 주고, 라운드가 끝나면 남은 값을 저장으로 되돌려 쓴다.
   * 그래서 `retry`·`restart`는 이 값을 **초기화하지 않고 그대로 이어받는다**(`freshRound`).
   */
  magazines: number
  /** 이번 라운드에 쓴 탄창 수. 라운드당 상한(`tuning.maxMagazinesPerRound`)의 근거다. */
  magazinesUsed: number
  isInOriginRoom: boolean
  /** 정답 방 체류 경과. 상한을 넘으면 자동 퇴장한다. */
  originStayElapsedMs: number
  /** 재입장까지 남은 쿨타임. 0이어야 입장할 수 있다. */
  originCooldownMs: number
  phaseElapsedMs: number
  huntEndReason: HuntEndReason | null
  score: number
  /**
   * 지금 이어지고 있는 연속 정답 수(`combo.ts`). 0이면 콤보 없음.
   *
   * **오답으로만 0이 된다. 시간으로는 절대 끊기지 않는다** - 이 게임은 관찰 게임이지 속도 게임이
   * 아니므로, 방을 훑는 30초가 콤보를 끊으면 최적 전략이 "관찰을 멈추고 빨리 쏘기"로 뒤집힌다.
   * `onTick`이 이 필드를 건드리지 않는 것이 그 규칙의 구현 전부다.
   *
   * 선택 필드인 이유는 `FoundEntry.combo`와 같다 - 없으면 콤보 없음으로 읽는다(`currentCombo`).
   * 리듀서가 만드는 상태에는 언제나 들어 있다.
   */
  combo?: number
  /** 이 라운드에 도달한 최고 콤보. 결과 화면이 자랑거리로 쓴다. */
  maxCombo?: number
  /**
   * 무오답 전량 발견을 달성했는가(`combo.ts`의 `isPerfectRun`).
   *
   * 마지막 발견의 순간에 확정되고, 그 뒤 리빌·결과까지 그대로 실려 간다.
   * 점수 쪽에서 다시 판정하지 않는 이유: 결과 화면이 보는 상태에는 "오답이 몇 번이었나"가 남아
   * 있지 않다(콤보가 이미 0으로 지워졌을 수 있다). 판정은 그 정보가 살아 있는 유일한 순간에 한다.
   */
  hasPerfect?: boolean
  /**
   * 걸기(M7)를 선언했는가. 선언 뒤 발견은 ×2, 빗나감은 `raiseMissCost`발(둘 다 `hunt.ts`·`raise.ts`가 정한다).
   * `onTick`이 이 필드를 건드리지 않는다 - 걸기의 조건은 잔탄이지 시간이 아니다(`raise.ts` 머리말).
   * 선택 필드인 이유는 `combo`와 같다 - 이 필드 이전의 상태·픽스처가 그대로 서고, 없으면 걸지 않은 것으로 읽는다.
   */
  raised?: boolean
}>

export type GameEvent =
  | Readonly<{ type: 'tick'; dtMs: number }>
  | Readonly<{ type: 'shoot'; hit: HitTarget | null }>
  /** 걸기(M7). 인자가 없다 - 선언에는 크기가 없고(배수는 규칙의 것), 라운드에 한 번뿐이다. */
  | Readonly<{ type: 'raise' }>
  /**
   * 탄창 하나를 쓴다. **잔탄 0에서의 유일한 부활 경로다.**
   *
   * 인자가 없는 것이 규칙이다 - 탄창은 개체가 아니라 개수이고, 어느 것을 쓸지 고르는 일이
   * 존재하지 않는다. 조준도 위치도 없으므로 이 이벤트는 HUD 버튼에서 곧장 온다.
   */
  | Readonly<{ type: 'useMagazine' }>
  | Readonly<{ type: 'toggleOrigin' }>
  | Readonly<{ type: 'skipPhase' }>
  /**
   * **같은 방, 새 변화.** 방 시드를 유지한 채 시도 번호만 올린다.
   * 튜토리얼의 재도전은 이쪽이다 - 방을 외운 것은 살리고 정답표만 버린다.
   */
  | Readonly<{ type: 'retry' }>
  /** **새 방.** 시드를 통째로 갈고 시도 번호를 0으로 되돌린다. 자유 모드의 "한 판 더"다. */
  | Readonly<{ type: 'restart'; seed: string }>

/** 사운드 식별자. 재생은 UI의 몫이고 **언제 울릴지가 규칙**이라 여기서 정한다. */
export type SoundId =
  | 'shot-hit'
  | 'shot-miss'
  | 'shot-blocked'
  | 'ammo-low'
  /** 탄환이 늘었다. 정답 방 리필도 월드 아이템도 사라진 지금은 **탄창을 썼을 때만** 울린다. */
  | 'ammo-refill'
  | 'warp-in'
  | 'warp-out'
  | 'phase-change'
  | 'round-over'

/**
 * 리듀서가 바깥 세계에 요청하는 일.
 *
 * 리듀서가 직접 하지 않는 이유는 테스트다 - "정답 방에 들어가면 워프가 일어나는가"를
 * Three.js 씬 없이 배열 비교로 검증할 수 있어야 한다.
 */
export type Effect =
  | Readonly<{ type: 'phaseEnter'; phase: Phase; previous: Phase }>
  /** 원본↔변경본 씬 전환. `warpDissolveSeconds` 동안 디졸브한다. 카메라는 건드리지 않는다. */
  | Readonly<{ type: 'warpTo'; origin: boolean }>
  /**
   * 히트 프록시 재구축(6.1절 빈 셀 히트).
   * 이게 빠지면 '제거' 동사가 **조준 자체 불가능**해진다 - 없는 물건은 광선이 통과한다.
   */
  | Readonly<{ type: 'syncProxies' }>
  | Readonly<{ type: 'playSound'; id: SoundId }>
  | Readonly<{ type: 'foundChange'; changeId: string; targetId: InstanceId; remaining: number }>
  /**
   * 컨셉시트 4.3절의 "잔탄 2발 경고 연출" 트리거. 임계선을 **넘는 순간** 한 번만 나온다.
   * 예외가 하나 있다: 잔탄이 0이 됐는데 쓸 수 있는 탄창이 남아 있으면 다시 한 번 나온다 -
   * 그 상황은 라운드의 끝이 아니라 "재장전하라"는 지시이고, 화면만 봐서는 라운드 끝과
   * 구별되지 않으므로 이 효과가 유일한 안내다.
   */
  | Readonly<{ type: 'lowAmmo'; ammo: number }>
  /**
   * 탄창을 썼다. `ammo`는 **실제로 채워진 뒤의 잔탄**이라 상한에 걸리면 `ammoPerMagazine`을
   * 다 받지 못한 결과일 수 있다. 요청값이 아니라 실현값을 싣는 이유는 HUD가 거짓말을 하지
   * 않게 하기 위해서다. `remaining`은 **소모 뒤** 계정에 남은 탄창 수다.
   */
  | Readonly<{ type: 'magazineUsed'; ammo: number; remaining: number }>
  /** 쿨타임 때문에 정답 방 입장이 거부됐다. 조용히 씹으면 버튼이 고장 난 것으로 보인다. */
  | Readonly<{ type: 'originEntryDenied'; remainingMs: number }>
  /**
   * 콤보가 한 칸 올랐다. **`foundChange`보다 먼저 나온다** - 발견 연출과 소리가 이 값을 읽어
   * 자막 크기와 음정을 정하기 때문이다(콤보마다 반음이 올라간다).
   *
   * `multiplier`를 같이 싣는 이유는 배수 규칙이 `combo.ts` 하나에만 있어야 하기 때문이다.
   * HUD가 콤보 수에서 배수를 다시 계산하면 상한(3.0)이 두 곳에 생기고, 튜닝 한 번에 갈라진다.
   */
  | Readonly<{ type: 'comboUp'; combo: number; multiplier: number }>
  /**
   * 콤보가 끊겼다. **오답일 때만 나온다.** 쌓인 것이 없었으면(콤보 0에서의 오답) 나오지 않는다 -
   * 잃은 것이 없는데 "끊겼다"를 띄우면 그 연출은 곧 배경이 되어 진짜 손실을 알리지 못한다.
   *
   * @param at 끊긴 순간의 콤보 수. **잃어버린 크기**이므로 연출의 세기가 여기서 갈린다.
   */
  | Readonly<{ type: 'comboBroken'; at: number }>
  /** 걸기(M7)가 성립했다. HUD 칩이 두 겹이 되는 순간이고, 거부는 이 효과 없이 `shot-blocked` 소리만 난다. */
  | Readonly<{ type: 'raised' }>

/** 리듀서 결과. 상태는 항상 새 객체이거나 입력과 동일 참조(변화 없음)다. */
export type Step = Readonly<{ state: GameState; effects: readonly Effect[] }>

/**
 * 변화 생성기의 모양. `options`는 매치의 사람 체인저 마감(잔여 예산 채움, `excludeIds`)이 쓰고 싱글은 넘기지 않는다 -
 * 타입을 하나로 두는 이유는 `match/matchTypes.ts`가 이것을 그대로 별칭으로 쓰기 때문이다. 둘로 갈라 두면
 * 한쪽에만 옵션이 붙은 사실이 눈에 안 띈다.
 */
export type GenerateChangesFn = (
  original: RoomState,
  seed: string,
  tuning: Tuning,
  catalog: Catalog,
  plan: FloorPlan,
  options?: GenerateChangesOptions,
) => { mutated: RoomState; changes: readonly Change[] }

export type ResolveShotFn = (state: GameState, target: HitTarget | null) => ShotOutcome

export type ComputeScoreFn = (state: GameState, tuning: Tuning) => number

/** 리듀서가 필요로 하는 바깥 세계 전부. 앱은 부트스트랩에서 한 번 만들어 계속 넘긴다. */
export type GameDeps = Readonly<{
  tuning: Tuning
  catalog: Catalog
  plan: FloorPlan
  /**
   * 방 생성 옵션(단계별 목표 오브젝트 수 등). 생략하면 생성기 기본값이다.
   *
   * `plan`과 나란히 두는 이유: 튜토리얼 단계는 **같은 평면도에서 밀도만 다른** 경우가 있고
   * (1·2단계가 같은 원룸에서 30개/40개), 그 차이는 튜닝이 아니라 방 생성의 입력이다.
   * `tuning`에 섞으면 URL 덮어쓰기 표에 들어가야 하는데, 이 값은 재시작을 건너뛰고는
   * 반영될 수 없으므로 그 표의 계약("재빌드 없이 즉시")을 지킬 수 없다.
   */
  roomOptions?: GenerateOptions
  generateChanges: GenerateChangesFn
  resolveShot: ResolveShotFn
  computeScore: ComputeScoreFn
}>

/* --- 셀렉터 ------------------------------------------------------------------ */

/**
 * 변화 생성에 넘길 시드. **방 시드와 갈리는 유일한 지점이다.**
 *
 * 파생 규칙을 함수 하나로 묶어 둔 이유는 재현 때문이다 - 개발자가 `?attempt=3`으로 특정 변화
 * 조합을 다시 부르려면 "그때 어떤 문자열이 들어갔는가"를 한 곳에서 읽을 수 있어야 한다.
 * (`changeGen`이 여기에 `:changes`를 덧붙여 방 생성과 수열을 한 번 더 가른다)
 */
export function changeSeedFor(state: GameState): string {
  return `${state.seed}${ATTEMPT_SEED_PREFIX}${state.attempt}`
}

/** 재도전 중인가. 결과 화면이 "N회차"를 띄울지 판단하는 근거다. */
export function isRetry(state: GameState): boolean {
  return state.attempt > FIRST_ATTEMPT
}

/** 아직 발견되지 않은 변화. 리빌이 공개할 목록이자 리빌 길이의 근거다. */
export function unfoundChanges(state: GameState): readonly Change[] {
  const foundIds = new Set(state.found.map((entry) => entry.changeId))
  return state.changes.filter((change) => !foundIds.has(change.id))
}

/** 발견 목록은 12개 이하이므로 선형 탐색으로 충분하다. 인덱스를 들고 다니면 불변성만 복잡해진다. */
export function isChangeFound(state: GameState, changeId: string): boolean {
  return state.found.some((entry) => entry.changeId === changeId)
}

/**
 * 지금 이어지는 콤보 수. 기록이 없는 상태는 **콤보 없음**으로 읽는다.
 *
 * 읽는 곳이 셋(HUD·사운드·점수)이므로 `?? 0`을 세 번 적지 않기 위해 셀렉터로 둔다.
 * 세 곳이 각자 기본값을 적으면 그중 하나가 `?? 1`이 되는 날이 오고, 그날 점수는 조용히 부풀어 오른다.
 */
export function currentCombo(state: GameState): number {
  return state.combo ?? NO_COMBO
}

/** 이 라운드의 최고 콤보. */
export function bestCombo(state: GameState): number {
  return state.maxCombo ?? NO_COMBO
}

/** 무오답 전량 발견인가. 판정 자체는 마지막 발견의 순간에 끝나 있다(`onHit`). */
export function isPerfectRound(state: GameState): boolean {
  return state.hasPerfect === true
}

/** 걸기(M7)를 선언했는가. 기록이 없는 상태는 걸지 않은 것으로 읽는다(`currentCombo`와 같은 이유). */
export function hasRaised(state: GameState): boolean {
  return state.raised === true
}

/**
 * 지금 걸 수 있는가. 페이즈 게이트만 여기 것이고 조건(잔탄 · 1회)은 리프(`raise.canRaise`)가 답한다 -
 * 매치(`match/matchShoot.onRaise` · `matchView.canRaise`)가 같은 리프를 부르므로 비교식이 두 곳에 살지 않는다.
 * `canUseMagazine`·`canEnterOriginRoom`과 같은 자리이고, 뷰는 이 값을 그대로 옮긴다.
 */
export function canRaiseNow(state: GameState, tuning: Tuning): boolean {
  return state.phase === 'hunt' && canRaise(state.ammo, hasRaised(state), tuning)
}

/**
 * 현재 페이즈에 정해진 길이(ms). `null`이면 시간으로 끝나지 않는다.
 *
 * 결과 화면만 `null`이다 - "한 판 더"는 사람이 눌러야 하며, 자동으로 넘어가면
 * 이 프로젝트의 유일한 판정 지표(컨셉시트 8절)를 관측할 기회가 사라진다.
 */
export function phaseDurationMs(state: GameState, tuning: Tuning): number | null {
  switch (state.phase) {
    case 'watch':
      return tuning.watchSeconds * MS_PER_SECOND
    case 'hunt':
      return tuning.huntSeconds * MS_PER_SECOND
    case 'reveal':
      return revealDurationMs(unfoundChanges(state).length, tuning)
    case 'result':
      return null
  }
}

/** 남은 페이즈 시간(ms). HUD 타이머가 쓴다. */
export function phaseRemainingMs(state: GameState, tuning: Tuning): number | null {
  const duration = phaseDurationMs(state, tuning)
  if (duration === null) return null
  return Math.max(0, duration - state.phaseElapsedMs)
}

/**
 * 지금 정답 방에 들어갈 수 있는가. 버튼 비활성화 판단에 그대로 쓴다.
 * 페이즈 게이트만 여기 것이고 방문 가능 여부(밖 + 쿨타임 0)는 리프가 답한다 - 매치는 같은 리프에
 * 문 토큰·역할을 덧붙이므로, 비교식을 여기 한 벌 더 두면 입장 조건이 두 곳에 산다.
 */
export function canEnterOriginRoom(state: GameState): boolean {
  return state.phase === 'hunt' && canStartOriginVisit(state)
}

/** 경고 연출을 계속 띄워야 하는 상태인가(효과 `lowAmmo`는 진입 순간 1회, 이쪽은 지속 상태). */
export function isLowAmmo(state: Pick<GameState, 'ammo'>, tuning: Tuning): boolean {
  return state.ammo > 0 && state.ammo <= tuning.lowAmmoWarningAt
}

/**
 * 오답 뒤의 잔탄이 임계선을 **막 넘었는가**(진입 순간 1회 - `lowAmmo` 효과와 `ammo-low` 소리의 조건).
 * `isLowAmmo`(지속)와 짝이고, 싱글 `onMiss`와 매치 `matchShoot.onMiss`가 같은 함수를 부른다 -
 * 이 비교식을 두 리듀서가 각자 적으면 `?lowammo=`가 한쪽에서만 울리는 날이 온다.
 *
 * **구간으로 판정한다**(`before > 임계 ≥ after`). 등호(`after === 임계`)였을 때 걸기(M7) 뒤 빗나감이 2발씩 빠지면
 * 3 → 1로 임계선(2)을 건너뛰어 경고가 한 번도 나지 않았다 - 컨셉시트 4.3절의 잔탄 경고가 걸기 뒤 절반의 판에서 사라진 것이다.
 * `after > 0`은 그대로다 - 잔탄 0은 별도 분기(탄창 있음 → `lowAmmo` · 없음 → 라운드 끝/관전)가 맡는다.
 */
export function crossesLowAmmoThreshold(before: number, after: number, tuning: Tuning): boolean {
  return after > 0 && after <= tuning.lowAmmoWarningAt && before > tuning.lowAmmoWarningAt
}

/* --- 탄창 (계정 자원의 라운드 쪽 얼굴) --------------------------------------------- */

/*
 * 아래 셀렉터 중 `phase`를 읽지 않는 것(`isLowAmmo`·`magazineUsesLeft`·`hasRoomForFullMagazine`)은
 * `Pick<GameState, …>`으로 받는다. 매치 리듀서가 술래 좌석(같은 이름의 필드를 가진 다른 타입)을
 * 그대로 넘겨 **같은 함수**를 부르게 하기 위해서다 - 사본을 만들면 튜닝 한 번에 두 값이 갈라진다.
 * `canUseMagazine`·`canEnterOriginRoom`은 `phase`를 읽으므로 그대로 `GameState`를 받는다.
 */

/**
 * 탄환 상한. **시작 탄환과 같은 값이다.**
 *
 * 별도 튜닝 키를 두지 않은 이유가 둘이다.
 *  1. **손잡이를 늘리지 않는다.** 상한이 시작값과 독립이면 "16으로 시작해 24까지 쌓임" 같은
 *     조합이 생기고, 그 조합의 체감을 설명하려면 두 숫자를 동시에 이야기해야 한다.
 *  2. **HUD가 거짓말을 하지 않는다.** 잔탄 게이지는 `tuning.startAmmo`를 만점으로 그린다.
 *     그 위로 쌓이면 게이지가 넘치고, 넘치는 게이지는 정보가 아니라 버그로 읽힌다.
 *
 * 상한 자체를 두는 이유는 탄창의 의미다. 없으면 최적 전략이 "가진 탄창을 먼저 다 털어 넣기"로
 * 수렴하고, 그 순간 잔탄 긴장이 라운드에서 통째로 사라진다.
 */
export function ammoCapacity(tuning: Tuning): number {
  return tuning.startAmmo
}

/**
 * 탄창 하나를 쓴 뒤의 잔탄. `hasRoomForFullMagazine`이 이미 통과했으므로 실제로는 정확히 `ammoPerMagazine`이
 * 들어가고, 클램프는 튜닝 오버라이드(`?magAmmo=`)로 회복량이 상한을 넘는 경우에 대한 방어로만 남는다.
 * 싱글 `onUseMagazine`과 매치 `matchShoot.onUseMagazine`이 같은 함수를 부른다 - 한 줄이지만 사본을 두지 않는다.
 */
export function refilledAmmo(ammo: number, tuning: Tuning): number {
  return Math.min(ammoCapacity(tuning), ammo + tuning.ammoPerMagazine)
}

/** 이번 라운드에 아직 쓸 수 있는 탄창 수. 계정 보유량과 라운드 상한 중 **작은 쪽**이다. */
export function magazineUsesLeft(
  state: Pick<GameState, 'magazines' | 'magazinesUsed'>,
  tuning: Tuning,
): number {
  const roundLeft = Math.max(0, tuning.maxMagazinesPerRound - state.magazinesUsed)
  return Math.min(state.magazines, roundLeft)
}

/**
 * 지금 탄창을 쓸 수 있는가. HUD 버튼의 활성/비활성이 이 값 하나로 정해진다.
 *
 * 조건이 넷이고 각각이 다른 것을 막는다.
 *  - **수색 페이즈에서만.** 외우기·리빌·결과에는 잔탄이라는 개념이 없다.
 *  - **한 발이라도 흘리게 되면 거부.** 빈 자리가 `ammoPerMagazine`보다 작으면 못 쓴다.
 *    기본값에서는 **6발을 쏜 뒤부터** 쓸 수 있다는 뜻이다(상한 16 - 회복 6 = 잔탄 10 이하).
 *
 *    처음에는 "잔탄이 가득일 때만" 막았는데, 그러면 잔탄 15에서 눌러 1발만 들어오고
 *    나머지 5발이 증발한다. 상한에 걸려 0발만 들어오는 경우만 벌이 아니라
 *    **일부만 들어오는 경우도 똑같이 손해**이고, 사람은 그 손해를 누른 뒤에야 안다.
 *    (상태모델 12절: 오답이 아닌 것을 벌하지 않는다.) 올바른 응답은 "썼다 치고 버리기"가
 *    아니라 "나중에 오라"다.
 *
 *    **상수 6을 박지 않고 튜닝값에서 유도하는 이유**는 `startAmmo`가 레벨마다 다르기 때문이다
 *    (`levels.ts`). 경계를 손으로 적으면 레벨 하나가 상한을 바꾸는 순간 그 레벨에서만
 *    조용히 탄이 새고, 그건 화면에 증상이 안 나타나 찾기 어려운 종류의 어긋남이다.
 *  - **보유량 0이면 거부.**
 *  - **라운드당 상한.** 이것이 없으면 탄창을 많이 가진 사람은 라운드를 무한히 연장할 수 있고,
 *    "잔탄이 마르면 끝난다"는 규칙이 돈으로 사라진다. 상한을 두는 대신 그 안에서는
 *    **잔탄 0에서도 쓸 수 있어야 한다** - 그게 이 자원의 존재 이유이기 때문이다.
 *
 * **정답 방 안이라고 막지 않는다.** 재장전은 방에 손을 대는 행위가 아니라 HUD의 일이고,
 * 빈 총을 든 채로 비교하러 들어간 사람에게 "나갔다 와서 누르라"고 할 이유가 없다.
 */
export function canUseMagazine(state: GameState, tuning: Tuning): boolean {
  if (state.phase !== 'hunt') return false
  if (!hasRoomForFullMagazine(state, tuning)) return false
  return magazineUsesLeft(state, tuning) > 0
}

/**
 * 탄창 하나가 **한 발도 흘리지 않고** 들어갈 자리가 있는가.
 *
 * 이 조건이 `canUseMagazine`과 `onUseMagazine`의 상한 클램프를 잇는 불변식이다:
 * 여기가 참일 때만 쓸 수 있으므로, 실제로 채워지는 양은 **언제나 정확히 `ammoPerMagazine`**이다.
 * 클램프(`Math.min`) 쪽은 그래서 도달하지 않는 방어선으로 남는다 - 지우지 않는 이유는
 * 튜닝 오버라이드(`?magAmmo=`)로 회복량이 상한을 넘게 설정될 수 있기 때문이다.
 */
export function hasRoomForFullMagazine(state: Pick<GameState, 'ammo'>, tuning: Tuning): boolean {
  return ammoCapacity(tuning) - state.ammo >= tuning.ammoPerMagazine
}

/* --- 생성 ------------------------------------------------------------------- */

/**
 * 외우기 페이즈에 선 라운드 하나.
 *
 * `createGame`(새 방)과 `retry`(같은 방)가 **같은 함수**로 상태를 세운다.
 * 초기화 목록을 두 곳에 적으면 한쪽만 빠뜨린 필드가 생기고, 그게 곧
 * "재도전인데 탄환이 안 찼다" · "지난 판의 발견이 남아 있다" 류의 버그다.
 */
function freshRound(
  seed: string,
  attempt: number,
  original: RoomState,
  tuning: Tuning,
  magazines: number,
): GameState {
  return {
    phase: 'watch',
    seed,
    attempt,
    original,
    // 외우기 페이즈에는 변화가 없다. 같은 참조를 넣어 "아직 안 만들어졌다"를 타입이 아닌 값으로 표현한다.
    mutated: original,
    changes: [],
    found: [],
    ammo: tuning.startAmmo,
    // 탄창은 계정의 것이라 **초기화하지 않고 이어받는다.** 여기서 되돌리면 재도전 한 번에
    // 사람이 산 물건이 되살아나거나 사라진다.
    magazines,
    magazinesUsed: NO_MAGAZINE_USED,
    ...NO_ORIGIN_VISIT,
    phaseElapsedMs: 0,
    huntEndReason: null,
    score: 0,
    ...NO_COMBO_YET,
  }
}

/**
 * 콤보 삼총사의 초기값. 라운드 시작과 수색 진입이 **같은 객체**를 편다.
 * 세 필드를 두 곳에 손으로 적으면 그중 하나가 빠지는 날이 오고, 그때 지난 판의 퍼펙트가
 * 이번 판 결과 화면에 그대로 뜬다 - 조용히 거짓말을 하는 종류의 버그다.
 */
const NO_COMBO_YET = {
  combo: NO_COMBO,
  maxCombo: NO_COMBO,
  hasPerfect: false,
  // 걸기(M7)도 같은 객체를 편다 - 라운드마다 되돌아가는 깃발이고, 따로 두면 `retry`·`restart`·수색 진입 중 한 곳이 빠진다.
  raised: false,
} as const

/**
 * 라운드를 시작한다. 시드가 방을 정하고, 방과 시도 번호가 변화를 정하고, 변화가 라운드를 정한다.
 * 변화는 아직 만들지 않는다 - 외우기 페이즈가 보는 것은 정의상 `original`이기 때문이다(2절).
 *
 * @param attempt 몇 번째 시도인가(0부터). 방에는 영향이 없고 **변화 시드에만** 들어간다.
 *                URL의 `?attempt=3`이 여기로 들어와 특정 변화 조합을 그대로 다시 부른다.
 * @param magazines 계정이 들고 있는 탄창 수(`progress.ts`에서 온다).
 *                  **기본값이 0인 것이 의도다** - 세션이 넘겨 주는 것을 잊으면 탄창이 없는 판이
 *                  되지, 공짜 탄창이 생기지 않는다. 실패의 방향을 안전한 쪽으로 고정한다.
 */
export function createGame(
  seed: string,
  tuning: Tuning,
  catalog: Catalog,
  plan: FloorPlan,
  roomOptions?: GenerateOptions,
  attempt: number = FIRST_ATTEMPT,
  magazines = 0,
): GameState {
  if (seed.trim() === '') {
    throw new RangeError('createGame: 시드가 비어 있다 - 재현할 수 없는 방은 만들지 않는다')
  }
  if (!Number.isSafeInteger(attempt) || attempt < FIRST_ATTEMPT) {
    throw new RangeError(
      `createGame: attempt는 ${FIRST_ATTEMPT} 이상의 정수여야 한다 (받은 값: ${String(attempt)})`,
    )
  }

  return freshRound(
    seed,
    attempt,
    generateBaseRoom(plan, seed, catalog, roomOptions),
    tuning,
    magazines,
  )
}

/* --- 페이즈 전환 -------------------------------------------------------------- */

function idle(state: GameState): Step {
  return { state, effects: [] }
}

/**
 * 수색 시작. 여기서 처음으로 방이 두 벌이 된다.
 *
 * 변화가 하나도 없으면 라운드가 성립하지 않으므로 던진다. 조용히 넘어가면
 * "아무리 찾아도 없는 라운드"를 플레이어가 5분 동안 뒤지게 되고, 그 5분은 되돌릴 수 없다.
 */
function enterHunt(state: GameState, deps: GameDeps, previous: Phase): Step {
  /*
   * **방 시드가 아니라 변화 시드를 넘긴다**(`seed:aN`). 시도 번호가 여기서만 섞이므로
   * 방은 그대로인 채 변화만 갈린다 - 재도전의 의미가 통째로 이 한 줄에 걸려 있다.
   *
   * 방 생성과의 스트림 분리(`:changes`)는 생성기가 스스로 한다. 그 규칙까지 여기서 흉내 내면
   * 분리 규칙이 두 파일에 생기고, 한쪽만 바뀌는 날이 온다.
   */
  const seed = changeSeedFor(state)

  const { mutated, changes } = deps.generateChanges(
    state.original,
    seed,
    deps.tuning,
    deps.catalog,
    deps.plan,
  )

  if (changes.length === 0) {
    throw new Error(
      `enterHunt: 변화가 0개다 (변화 시드 '${seed}'). 허용 동사나 방 밀도를 확인하라`,
    )
  }

  const next: GameState = {
    ...state,
    phase: 'hunt',
    mutated,
    changes,
    found: [],
    ammo: deps.tuning.startAmmo,
    // 보유 탄창은 그대로 이어받고(계정의 것이다) **사용 횟수만** 이 라운드 기준으로 되돌린다.
    magazinesUsed: NO_MAGAZINE_USED,
    // 원래 방 셋도 `NO_COMBO_YET`과 같은 이유로 같은 객체를 편다(리프의 상수. 리빌 진입은 이걸 펴지 않는다).
    ...NO_ORIGIN_VISIT,
    phaseElapsedMs: 0,
    huntEndReason: null,
    // 외우기에서 수색으로 넘어올 때도 되돌린다. `skipPhase`로 들어오는 경로가 `freshRound`를 거치지 않는다.
    ...NO_COMBO_YET,
  }

  return {
    state: next,
    effects: [
      { type: 'phaseEnter', phase: 'hunt', previous },
      { type: 'warpTo', origin: false },
      // 순서가 중요하다: 변경본으로 바뀐 뒤에 그 방 기준으로 프록시를 세워야 한다.
      { type: 'syncProxies' },
      { type: 'playSound', id: 'phase-change' },
    ],
  }
}

function enterReveal(state: GameState, previous: Phase, reason: HuntEndReason): Step {
  const next: GameState = {
    ...state,
    phase: 'reveal',
    phaseElapsedMs: 0,
    huntEndReason: reason,
    isInOriginRoom: false,
    originStayElapsedMs: 0,
  }

  const effects: Effect[] = [{ type: 'phaseEnter', phase: 'reveal', previous }]
  // 정답 방에 있는 채로 라운드가 끝날 수 있다(체류 중 타임아웃). 리빌은 변경본에서 연출한다.
  if (state.isInOriginRoom) effects.push({ type: 'warpTo', origin: false })
  effects.push({ type: 'playSound', id: 'phase-change' })

  return { state: next, effects }
}

function enterResult(state: GameState, deps: GameDeps, previous: Phase): Step {
  const settled: GameState = { ...state, phase: 'result', phaseElapsedMs: 0, isInOriginRoom: false }
  const next: GameState = { ...settled, score: deps.computeScore(settled, deps.tuning) }

  return {
    state: next,
    effects: [
      { type: 'phaseEnter', phase: 'result', previous },
      { type: 'playSound', id: 'round-over' },
    ],
  }
}

/* --- 정답 방 (컨셉시트 4.4 - 워프 방식) ------------------------------------------- */

/**
 * 정답 방에 들어간다. **탄환은 한 발도 주지 않는다.**
 *
 * 컨셉시트 4.4는 정답 방에 "참조 + 보급기지" 이중 역할을 줬지만, 실제로 돌려 보니
 * 그 둘이 서로를 흐리게 만들었다 - 원본 방에 가는 이유가 "비교하려고"가 아니라
 * "탄환이 떨어져서"가 됐고, 그래서 정답 방 왕복이 비교 행위가 아니라 보급 행위가 됐다.
 * 보급을 계정 탄창으로 내보내면(`magazines.ts`) 이곳은 비교하러 가는 곳 하나로 남는다.
 */
function enterOriginRoom(state: GameState): Step {
  const next: GameState = { ...state, ...enterOriginVisit(state) }

  return {
    state: next,
    effects: [
      { type: 'warpTo', origin: true },
      { type: 'playSound', id: 'warp-in' },
    ],
  }
}

/**
 * 정답 방에서 나온다. 수동 퇴장과 체류 초과 자동 퇴장이 **같은 경로**를 쓴다 -
 * 갈라 두면 자동 퇴장에만 쿨타임을 거는 식의 구멍이 생기고, 그 구멍이 곧 깜빡비교다.
 * (체류·쿨타임 산술은 `originVisit.ts`. 여기는 그 결과를 상태에 얹고 효과를 낸다.)
 */
function exitOriginRoom(state: GameState, tuning: Tuning): Step {
  const next: GameState = { ...state, ...exitOriginVisit(state, tuning) }

  return {
    state: next,
    effects: [
      { type: 'warpTo', origin: false },
      { type: 'syncProxies' },
      { type: 'playSound', id: 'warp-out' },
    ],
  }
}

/* --- 이벤트 처리 -------------------------------------------------------------- */

/**
 * 원래 방 타이머를 흘린다. 산술은 `tickOriginVisit`이 하고, 여기는 두 가지만 덧붙인다 -
 * 체류 상한을 넘겼으면 **수동 퇴장과 같은 함수**(`exitOriginRoom`)로 효과까지 내고,
 * 아무것도 안 바뀌었으면(밖 + 쿨타임 0) 같은 참조를 돌려준다.
 */
function advanceOriginTimers(state: GameState, dtMs: number, tuning: Tuning): Step {
  const ticked = tickOriginVisit(state, dtMs, tuning)
  if (ticked.exited) return exitOriginRoom(state, tuning)
  if (ticked.visit === state) return idle(state)
  return { state: { ...state, ...ticked.visit }, effects: [] }
}

function onPhaseTimeout(state: GameState, deps: GameDeps): Step {
  switch (state.phase) {
    case 'watch':
      return enterHunt(state, deps, 'watch')
    case 'hunt':
      return enterReveal(state, 'hunt', 'timeout')
    case 'reveal':
      return enterResult(state, deps, 'reveal')
    case 'result':
      return idle(state)
  }
}

function onTick(state: GameState, dtMs: number, deps: GameDeps): Step {
  if (!Number.isFinite(dtMs) || dtMs < 0) {
    throw new RangeError(`tick: dtMs는 0 이상의 유한수여야 한다 (받은 값: ${String(dtMs)})`)
  }
  if (dtMs === 0) return idle(state)

  let next: GameState = { ...state, phaseElapsedMs: state.phaseElapsedMs + dtMs }
  const effects: Effect[] = []

  // 정답 방 타이머는 수색 중에만 흐른다. 리빌·결과에서 쿨타임이 도는 것은 의미가 없다.
  if (next.phase === 'hunt') {
    const timers = advanceOriginTimers(next, dtMs, deps.tuning)
    next = timers.state
    effects.push(...timers.effects)
  }

  const duration = phaseDurationMs(next, deps.tuning)
  if (duration === null || next.phaseElapsedMs < duration) return { state: next, effects }

  const timedOut = onPhaseTimeout(next, deps)
  return { state: timedOut.state, effects: [...effects, ...timedOut.effects] }
}

/**
 * 정답 한 발.
 *
 * 콤보가 여기서 오르고, **발견 기록에 그 값이 함께 굳는다**(`FoundEntry.combo`).
 * 배수를 나중에 다시 계산하지 않기 위해서다 - 소급 계산은 오답으로 끊긴 구간까지 되살린다.
 */
function onHit(state: GameState, change: Change): Step {
  const combo = comboAfter(currentCombo(state), 'hit')
  const found: readonly FoundEntry[] = [
    ...state.found,
    // 걸기(M7)도 콤보처럼 **이 순간의 값**으로 굳는다. 걸지 않았으면 필드를 남기지 않는다(없음 = 걸지 않음).
    { changeId: change.id, foundAtMs: state.phaseElapsedMs, combo, ...(hasRaised(state) ? { raised: true } : {}) },
  ]
  const remaining = state.changes.length - found.length

  const next: GameState = {
    ...state,
    found,
    combo,
    maxCombo: Math.max(bestCombo(state), combo),
    /*
     * 퍼펙트는 **이 순간에만** 판정할 수 있다. 콤보는 오답으로만 끊기므로 "콤보가 변화 개수만큼
     * 쌓였다"가 곧 "오답이 한 발도 없었다"이고, 그 등식이 성립하는 마지막 시점이 여기다.
     * 결과 화면에서 다시 물으면 그때는 오답의 흔적이 이미 지워져 있다.
     */
    hasPerfect: isPerfectRun(combo, state.changes.length),
  }

  const effects: Effect[] = [
    // 소리와 자막이 콤보를 읽어야 하므로 발견 통지보다 먼저 낸다.
    { type: 'comboUp', combo, multiplier: comboMultiplier(combo) },
    { type: 'playSound', id: 'shot-hit' },
    { type: 'foundChange', changeId: change.id, targetId: change.targetId, remaining },
  ]

  if (remaining > 0) return { state: next, effects }

  const done = enterReveal(next, 'hunt', 'all-found')
  return { state: done.state, effects: [...effects, ...done.effects] }
}

/**
 * 콤보를 이 결과에 맞게 옮긴 상태와, 그때 내보낼 연출.
 *
 * 정답 경로(`onHit`)와 갈라 둔 이유: 정답은 발견 기록·퍼펙트까지 같이 건드리는 반면
 * 오답·중복 사격은 콤보만 손대기 때문이다. 한 함수로 묶으면 그 차이가 분기로 들어와,
 * "중복 사격이 콤보를 끊지 않는다"는 규칙이 조건문 깊숙이 숨는다.
 */
function withComboOutcome(state: GameState, outcome: ComboOutcome): Step {
  const before = currentCombo(state)
  const combo = comboAfter(before, outcome)

  if (!breaksCombo(before, outcome)) {
    // 콤보가 그대로면 상태도 그대로다. 같은 참조를 돌려 "변화 없음"을 참조 비교로 알 수 있게 한다.
    return combo === before ? idle(state) : { state: { ...state, combo }, effects: [] }
  }

  return { state: { ...state, combo }, effects: [{ type: 'comboBroken', at: before }] }
}

/** @param ammoCost `hunt.ts`가 정한 값. 여기서 1을 하드코딩하면 탄환 규칙이 두 곳에 생긴다. */
function onMiss(state: GameState, ammoCost: number, deps: GameDeps): Step {
  // **오답이 콤보를 끊는 유일한 사건이다.** 탄환보다 먼저 처리해 잔탄이 0이 되어 라운드가
  // 끝나는 경로에서도 `comboBroken` 연출이 빠지지 않게 한다.
  const broken = withComboOutcome(state, 'miss')
  // 0에서 멈추는 것은 `hunt.ammoAfterMiss`가 정한다 - 걸기 뒤 2발 비용에서 잔탄이 음수가 되면 탄창이 한 발을 잃는다.
  const ammo = ammoAfterMiss(broken.state.ammo, ammoCost)
  const next: GameState = { ...broken.state, ammo }
  const effects: Effect[] = [...broken.effects, { type: 'playSound', id: 'shot-miss' }]

  // 임계선을 **넘는 순간**에만 울린다. 매 오답마다 울리면 경고가 배경음이 되어 경고가 아니게 된다.
  if (crossesLowAmmoThreshold(broken.state.ammo, ammo, deps.tuning)) {
    effects.push({ type: 'lowAmmo', ammo }, { type: 'playSound', id: 'ammo-low' })
  }

  if (ammo > 0) return { state: next, effects }

  /*
   * **잔탄 0이 곧 라운드의 끝이던 규칙이 여기서 뒤집힌다.**
   *
   * 라운드 종료 판정보다 **탄창 검사를 먼저** 한다. 순서가 규칙이다 - 종료를 먼저 판정하면
   * 잔탄 0인 순간 페이즈가 리빌로 넘어가고, `canUseMagazine`의 첫 조건(`phase === 'hunt'`)이
   * 거짓이 되어 **탄창이 있어도 영원히 못 쓴다.** 잔탄 0에서 쓸 수 있다는 것이 이 자원의
   * 존재 이유이므로, 그 순간을 막는 코드는 자원 자체를 없애는 것과 같다.
   *
   * 그 사이 사격은 `hunt.canShoot`이 잔탄 0으로 막는다. 즉 이 구간은 "총을 못 쏘는 채로
   * 재장전을 기다리는 시간"이다. 사람이 아무것도 하지 않으면 수색 시간이 다 될 때까지 그대로이므로,
   * 여기서 울리는 경고가 **유일한 안내**다. 화면만 봐서는 라운드 끝과 구별되지 않는다.
   */
  if (canUseMagazine(next, deps.tuning)) {
    effects.push({ type: 'lowAmmo', ammo }, { type: 'playSound', id: 'ammo-low' })
    return { state: next, effects }
  }

  // 탄창까지 바닥났다. 4.3절의 관전 모드는 멀티 전용이라 싱글에서는 라운드를 끝낸다.
  const done = enterReveal(next, 'hunt', 'out-of-ammo')
  return { state: done.state, effects: [...effects, ...done.effects] }
}

/**
 * 탄창 하나를 쓴다. **잔탄 0에서도 통한다** - 그게 이 자원의 존재 이유다.
 *
 * 거부는 **조용히 씹지 않는다.** 탄창은 잔탄 0에서의 유일한 부활 수단이라, 눌렀는데 아무 일도
 * 일어나지 않으면 사람은 버튼이 고장 났다고 판단하고 그 자리에서 판을 버린다. 다만 수색 밖에서는
 * 버튼 자체가 없으므로(HUD가 그리지 않는다) 그때는 소리도 내지 않는다 - `shoot`과 같은 판단이다.
 *
 * 계정 보유량을 여기서 줄이는 것이 규칙이다. 저장으로 되돌려 쓰는 일은 세션 레이어가 맡지만,
 * **줄어드는 순간의 진실은 라운드 상태**여야 한다. 저장이 실패하는 브라우저에서도 이번 판의
 * 잔여 탄창은 정확해야 하기 때문이다(`progress.ts`의 "저장은 실패해도 된다" 계약).
 */
function onUseMagazine(state: GameState, deps: GameDeps): Step {
  if (state.phase !== 'hunt') return idle(state)
  if (!canUseMagazine(state, deps.tuning)) {
    return { state, effects: [{ type: 'playSound', id: 'shot-blocked' }] }
  }

  // 채우는 양은 `refilledAmmo` 하나가 정한다(매치도 같은 함수를 부른다).
  const ammo = refilledAmmo(state.ammo, deps.tuning)
  const magazines = state.magazines - 1

  return {
    state: { ...state, ammo, magazines, magazinesUsed: state.magazinesUsed + 1 },
    effects: [
      { type: 'magazineUsed', ammo, remaining: magazines },
      { type: 'playSound', id: 'ammo-refill' },
    ],
  }
}

function onShoot(state: GameState, hit: HitTarget | null, deps: GameDeps): Step {
  // 게이트(수색 페이즈 / 정답 방 / 탄환 0)는 `hunt.ts`가 소유한다.
  // 4.4절: 원본에는 변화가 없으므로 정답 방은 비교하는 곳이지 쏘는 곳이 아니다.
  if (!canShoot(state)) {
    // 수색 밖에는 사격 UI 자체가 없다. 반면 정답 방·탄환 0은 "눌렀는데 안 나간" 상황이라
    // 피드백이 없으면 버튼이 고장 난 것으로 읽힌다.
    // **탄환 0은 더 이상 라운드의 끝이 아니다** - 탄창이 남아 있으면 재장전한 뒤 다시 쏜다.
    if (state.phase !== 'hunt') return idle(state)
    return { state, effects: [{ type: 'playSound', id: 'shot-blocked' }] }
  }

  const outcome = deps.resolveShot(state, hit)
  switch (outcome.kind) {
    case 'hit':
      return onHit(state, outcome.change)
    /*
     * 상태모델 12절: 이미 득점된 변화를 다시 쏜 것은 오답이 아니다. 벌하면 억울한 실패가 된다.
     * **콤보도 끊지 않는다** - 탄환을 안 먹는 행위에 콤보만 벌을 주면 규칙이 앞뒤가 안 맞고,
     * 무엇보다 사람이 "찾은 곳을 또 쏠까 봐" 화면을 덜 훑게 된다. 관찰을 줄이는 규칙은
     * 관찰 게임에서 잘못된 규칙이다. 굳이 `withComboOutcome`을 거치는 이유는 그 규칙을
     * 주석이 아니라 코드로 남기기 위해서다(`comboAfter`가 유지를 보장한다).
     */
    case 'already-found': {
      const kept = withComboOutcome(state, 'already-found')
      return {
        state: kept.state,
        effects: [...kept.effects, { type: 'playSound', id: 'shot-blocked' }],
      }
    }
    case 'miss':
      // 걸기(M7) 뒤의 빗나감은 비싸다. 얼마나 비싼지는 `hunt.ts`가 정한다 - 여기서 곱하면 탄환 규칙이 두 곳에 생긴다.
      return onMiss(state, ammoCostForOutcome(outcome, hasRaised(state), deps.tuning), deps)
  }
}

/**
 * 걸기(M7). 수색 밖에서는 버튼 자체가 없으므로 조용히 무시하고, 수색 중 못 거는 상황(잔탄 부족 · 이미 걸었다)은
 * "눌렀는데 안 됐다"라 `shot-blocked`로 알린다(`onUseMagazine`과 같은 판단). 조건은 `raise.canRaise` 하나 -
 * 여기 `ammo >= 4`를 적으면 매치와 갈린다. 상태에 남는 것은 깃발 하나이고, 값어치는 다음 사격부터 `hunt.ts`가 매긴다.
 */
function onRaise(state: GameState, deps: GameDeps): Step {
  if (state.phase !== 'hunt') return idle(state)
  if (!canRaiseNow(state, deps.tuning)) {
    return { state, effects: [{ type: 'playSound', id: 'shot-blocked' }] }
  }
  return { state: { ...state, raised: true }, effects: [{ type: 'raised' }] }
}

function onToggleOrigin(state: GameState, deps: GameDeps): Step {
  if (state.phase !== 'hunt') return idle(state)
  if (state.isInOriginRoom) return exitOriginRoom(state, deps.tuning)

  if (state.originCooldownMs > 0) {
    return {
      state,
      effects: [
        { type: 'originEntryDenied', remainingMs: state.originCooldownMs },
        { type: 'playSound', id: 'shot-blocked' },
      ],
    }
  }

  return enterOriginRoom(state)
}

/** 페이즈를 사람이 앞당긴다. 결과 화면만 예외 - 거기서 나가는 길은 '한 판 더' 하나뿐이다. */
function onSkipPhase(state: GameState, deps: GameDeps): Step {
  switch (state.phase) {
    case 'watch':
      return enterHunt(state, deps, 'watch')
    case 'hunt':
      return enterReveal(state, 'hunt', 'skipped')
    case 'reveal':
      return enterResult(state, deps, 'reveal')
    case 'result':
      return idle(state)
  }
}

/**
 * 새 라운드가 외우기 페이즈에 설 때의 효과. `retry`와 `restart`가 **같은 목록**을 쓴다.
 *
 * `syncProxies`가 여기 있는 것이 가장 중요하다. 재도전은 같은 방을 재사용하므로 인스턴스 id까지
 * 같은데, 지난 시도의 제거 프록시가 남으면 **이번엔 멀쩡히 있는 물건 자리가 정답으로 잡힌다.**
 * 술래는 허공을 맞히고 정답 판정을 받는다 - 라운드가 조용히 거짓이 되는 종류의 버그다.
 */
function roundStartEffects(previous: Phase): readonly Effect[] {
  return [
    { type: 'phaseEnter', phase: 'watch', previous },
    { type: 'warpTo', origin: false },
    { type: 'syncProxies' },
    { type: 'playSound', id: 'phase-change' },
  ]
}

/**
 * "다른 변화로 다시" - **같은 방, 새 변화.**
 *
 * `generateBaseRoom`을 부르지 않는다. 같은 시드는 정의상 같은 방을 만들고(결정론),
 * 방은 불변이라(`verbs`가 새 상태를 돌려준다) 지난 시도가 `original`에 남긴 흔적이 없다.
 * 391개짜리 방을 8.5ms에 걸쳐 다시 구워 **같은 결과**를 얻을 이유가 없다.
 *
 * 대신 나머지는 전부 초기화한다 - 탄환·발견·페이즈·정답 방 카운터까지 `freshRound`가 맡는다.
 * **탄창만 예외다.** 계정의 것이므로 이어받는다 - 되돌리면 재도전 한 번에 사람이 쓴 물건이 되살아난다.
 */
function onRetry(state: GameState, deps: GameDeps): Step {
  const next = freshRound(
    state.seed,
    state.attempt + 1,
    state.original,
    deps.tuning,
    state.magazines,
  )

  return { state: next, effects: roundStartEffects(state.phase) }
}

/**
 * "한 판 더"(컨셉시트 8절의 단일 판정 지표) - **새 방.**
 * 어느 페이즈에서든 받는다 - 개발자가 수십 판을 돌리는 것이 이번 작업의 목적이고,
 * 그때 "지금은 리셋할 수 없는 페이즈"라는 벽은 순수한 마찰이다.
 *
 * 시도 번호는 0으로 돌아간다. 방이 바뀌면 세던 것도 바뀌는 방의 것이므로 이어 셀 대상이 없다.
 * 탄창은 방과 무관한 계정 값이라 여기서도 이어받는다.
 */
function onRestart(state: GameState, seed: string, deps: GameDeps): Step {
  const next = createGame(
    seed,
    deps.tuning,
    deps.catalog,
    deps.plan,
    deps.roomOptions,
    FIRST_ATTEMPT,
    state.magazines,
  )

  return { state: next, effects: roundStartEffects(state.phase) }
}

/**
 * 상태 머신 본체. 같은 (상태, 이벤트, deps)는 항상 같은 결과를 낸다.
 * 무시되는 이벤트는 입력 상태를 **그대로 반환**하므로 참조 비교로 "변화 없음"을 알 수 있다.
 */
export function reduce(state: GameState, ev: GameEvent, deps: GameDeps): Step {
  switch (ev.type) {
    case 'tick':
      return onTick(state, ev.dtMs, deps)
    case 'shoot':
      return onShoot(state, ev.hit, deps)
    case 'raise':
      return onRaise(state, deps)
    case 'useMagazine':
      return onUseMagazine(state, deps)
    case 'toggleOrigin':
      return onToggleOrigin(state, deps)
    case 'skipPhase':
      return onSkipPhase(state, deps)
    case 'retry':
      return onRetry(state, deps)
    case 'restart':
      return onRestart(state, ev.seed, deps)
  }
}
